import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Users,
  DollarSign,
  HelpCircle,
  ArrowLeft,
  Loader2,
  Lock,
  ExternalLink,
  Copy,
  Check,
  TrendingUp,
  Briefcase,
  Search,
  MessageSquare,
  ChevronRight,
  Star,
  AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  timeout: 120000,
  withCredentials: true
});

// Add Authorization header from localStorage token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('hireun_session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const InsightsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("quiz");
  const [copiedQuery, setCopiedQuery] = useState(null);
  
  // Job context from URL params or localStorage
  const [jobContext, setJobContext] = useState({
    job_title: searchParams.get("title") || "",
    company: searchParams.get("company") || "",
    seniority: searchParams.get("seniority") || "mid",
    industry: searchParams.get("industry") || "",
    location: searchParams.get("location") || "",
    language: localStorage.getItem("detected_language") || "en"
  });
  
  // Feature data
  const [quiz, setQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [salaryData, setSalaryData] = useState(null);
  const [candidateInsights, setCandidateInsights] = useState(null);
  const [isPremium, setIsPremium] = useState(true); // Default true for testing
  
  useEffect(() => {
    // Check user premium status
    const checkUserStatus = async () => {
      try {
        const response = await apiClient.get("/auth/me");
        setIsPremium(response.data.role === "premium" || response.data.role === "admin");
      } catch {
        setIsPremium(false);
      }
    };
    checkUserStatus();
  }, []);
  
  // Generate Quiz (FREE)
  const generateQuiz = async () => {
    if (!jobContext.company) {
      toast.error("Please provide a company name");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await apiClient.post("/quiz/generate", {
        company_name: jobContext.company,
        job_title: jobContext.job_title,
        industry: jobContext.industry,
        language: jobContext.language
      });
      
      setQuiz(response.data.quiz);
      setQuizAnswers({});
      setQuizResult(null);
      toast.success("Quiz generated!");
    } catch (error) {
      toast.error("Failed to generate quiz");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Submit Quiz
  const submitQuiz = async () => {
    if (!quiz) return;
    
    setIsLoading(true);
    try {
      const response = await apiClient.post("/quiz/submit", {
        quiz_id: quiz.quiz_id,
        answers: quizAnswers
      });
      
      setQuizResult(response.data.result);
      toast.success(`Quiz completed! Score: ${response.data.result.percentage}%`);
    } catch (error) {
      toast.error("Failed to submit quiz");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Get Salary Benchmark (PREMIUM)
  const getSalaryBenchmark = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.post("/insights/salary", {
        job_title: jobContext.job_title,
        seniority: jobContext.seniority,
        location: jobContext.location,
        company_name: jobContext.company,  // Added: pass company for specific benchmarks
        industry: jobContext.industry,
        language: jobContext.language
      });
      
      if (response.data.is_premium_feature) {
        setSalaryData({ isPremiumLocked: true, preview: response.data.preview });
      } else {
        setSalaryData(response.data.salary);
      }
    } catch (error) {
      toast.error("Failed to get salary data");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Get Candidate Insights (PREMIUM)
  const getCandidateInsights = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.post("/insights/candidates", {
        job_title: jobContext.job_title,
        company: jobContext.company,
        seniority: jobContext.seniority,
        industry: jobContext.industry,
        location: jobContext.location,
        language: jobContext.language
      });
      
      if (response.data.is_premium_feature) {
        setCandidateInsights({ isPremiumLocked: true, preview: response.data.preview });
      } else {
        setCandidateInsights(response.data.insights);
      }
    } catch (error) {
      toast.error("Failed to get candidate insights");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const copyToClipboard = (text, queryId) => {
    navigator.clipboard.writeText(text);
    setCopiedQuery(queryId);
    setTimeout(() => setCopiedQuery(null), 2000);
  };
  
  const getGradeColor = (grade) => {
    switch (grade) {
      case "A": return "text-green-600 bg-green-100";
      case "B": return "text-blue-600 bg-blue-100";
      case "C": return "text-yellow-600 bg-yellow-100";
      default: return "text-red-600 bg-red-100";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/app")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
            <h1 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Career Insights
            </h1>
            <div className="w-24" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Job Context Summary */}
        {jobContext.job_title && (
          <Card className="mb-6 bg-primary/5 border-primary/20">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-2 items-center">
                <Briefcase className="w-4 h-4 text-primary" />
                <span className="font-medium">{jobContext.job_title}</span>
                {jobContext.company && (
                  <>
                    <span className="text-muted-foreground">at</span>
                    <span className="font-medium">{jobContext.company}</span>
                  </>
                )}
                {jobContext.location && (
                  <Badge variant="outline">{jobContext.location}</Badge>
                )}
                {jobContext.seniority && (
                  <Badge variant="secondary">{jobContext.seniority}</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="quiz" className="gap-2">
              <HelpCircle className="w-4 h-4" />
              Company Quiz
              <Badge variant="secondary" className="ml-1 text-xs">Free</Badge>
            </TabsTrigger>
            <TabsTrigger value="salary" className="gap-2">
              <DollarSign className="w-4 h-4" />
              Salary Benchmark
              {!isPremium && <Lock className="w-3 h-3 text-amber-500" />}
            </TabsTrigger>
            <TabsTrigger value="network" className="gap-2">
              <Users className="w-4 h-4" />
              Similar Profiles
              {!isPremium && <Lock className="w-3 h-3 text-amber-500" />}
            </TabsTrigger>
          </TabsList>

          {/* Company Quiz Tab */}
          <TabsContent value="quiz">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-primary" />
                  Company Knowledge Quiz
                </CardTitle>
                <CardDescription>
                  Test your knowledge about {jobContext.company || "the company"} before your interview
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!quiz && !quizResult && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <HelpCircle className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-2">Ready to test your knowledge?</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Generate a personalized quiz about {jobContext.company || "the company"} to see how prepared you are for your interview.
                    </p>
                    <Button onClick={generateQuiz} disabled={isLoading || !jobContext.company}>
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating Quiz...
                        </>
                      ) : (
                        <>
                          Generate Quiz
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {quiz && !quizResult && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{quiz.quiz_title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {quiz.total_questions} questions • ~{quiz.estimated_time_minutes} minutes
                        </p>
                      </div>
                      <Progress value={(Object.keys(quizAnswers).length / quiz.total_questions) * 100} className="w-32" />
                    </div>
                    
                    <div className="space-y-6">
                      {quiz.questions.map((q, idx) => (
                        <div key={q.id} className="p-4 bg-muted/30 rounded-lg">
                          <div className="flex items-start gap-3">
                            <span className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                              {idx + 1}
                            </span>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant={q.difficulty === "easy" ? "secondary" : q.difficulty === "medium" ? "outline" : "destructive"}>
                                  {q.difficulty}
                                </Badge>
                                <Badge variant="outline">{q.category.replace("_", " ")}</Badge>
                              </div>
                              <p className="font-medium mb-3">{q.question}</p>
                              
                              {q.type === "multiple_choice" ? (
                                <div className="space-y-2">
                                  {q.options.map((opt, optIdx) => (
                                    <label
                                      key={optIdx}
                                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                        quizAnswers[q.id] === opt
                                          ? "border-primary bg-primary/5"
                                          : "border-border hover:border-primary/50"
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        name={`q-${q.id}`}
                                        value={opt}
                                        checked={quizAnswers[q.id] === opt}
                                        onChange={() => setQuizAnswers(prev => ({ ...prev, [q.id]: opt }))}
                                        className="sr-only"
                                      />
                                      <div className={`w-4 h-4 rounded-full border-2 ${
                                        quizAnswers[q.id] === opt ? "border-primary bg-primary" : "border-muted-foreground"
                                      }`}>
                                        {quizAnswers[q.id] === opt && (
                                          <div className="w-full h-full rounded-full bg-white scale-50" />
                                        )}
                                      </div>
                                      <span>{opt}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <textarea
                                  className="w-full p-3 border rounded-lg resize-none"
                                  rows={3}
                                  placeholder="Type your answer..."
                                  value={quizAnswers[q.id] || ""}
                                  onChange={(e) => setQuizAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-between items-center pt-4 border-t">
                      <Button variant="outline" onClick={() => { setQuiz(null); setQuizAnswers({}); }}>
                        Start Over
                      </Button>
                      <Button onClick={submitQuiz} disabled={isLoading || Object.keys(quizAnswers).length < quiz.total_questions}>
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit Quiz
                            <ChevronRight className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {quizResult && (
                  <div className="space-y-6">
                    {/* Score Summary */}
                    <div className="text-center py-6 bg-muted/30 rounded-lg">
                      <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${getGradeColor(quizResult.grade)}`}>
                        <span className="text-3xl font-bold">{quizResult.grade}</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-1">{quizResult.percentage}%</h3>
                      <p className="text-muted-foreground">
                        {quizResult.correct_answers} of {quizResult.total_questions} correct
                      </p>
                      <p className="mt-4 text-sm max-w-md mx-auto">{quizResult.feedback}</p>
                    </div>
                    
                    {/* Category Breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(quizResult.category_breakdown).map(([cat, data]) => (
                        <div key={cat} className="p-3 bg-muted/30 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground capitalize">{cat.replace("_", " ")}</p>
                          <p className="font-bold">{data.correct}/{data.total}</p>
                        </div>
                      ))}
                    </div>
                    
                    {/* Question Results */}
                    <div className="space-y-3">
                      <h4 className="font-semibold">Question Breakdown</h4>
                      {quizResult.question_results.map((r, idx) => (
                        <div key={idx} className={`p-4 rounded-lg border ${r.is_correct ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                          <div className="flex items-start gap-3">
                            {r.is_correct ? (
                              <Check className="w-5 h-5 text-green-600 mt-0.5" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-sm">{r.question}</p>
                              {!r.is_correct && (
                                <p className="text-sm text-red-600 mt-1">
                                  Correct: {r.correct_answer}
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground mt-2">{r.explanation}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-center pt-4">
                      <Button onClick={() => { setQuiz(null); setQuizAnswers({}); setQuizResult(null); }}>
                        Take Another Quiz
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Salary Benchmark Tab */}
          <TabsContent value="salary">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Salary Benchmark
                  {!isPremium && <Badge variant="outline" className="ml-2"><Lock className="w-3 h-3 mr-1" /> Premium</Badge>}
                </CardTitle>
                <CardDescription>
                  Market salary data for {jobContext.job_title || "this role"} in {jobContext.location || "your area"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!salaryData && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-semibold mb-2">Know Your Worth</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Get salary benchmarks and negotiation tips based on market data.
                    </p>
                    <Button onClick={getSalaryBenchmark} disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          Get Salary Data
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {salaryData?.isPremiumLocked && (
                  <div className="text-center py-8 bg-muted/30 rounded-lg">
                    <Lock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Premium Feature</h3>
                    <p className="text-muted-foreground mb-4">{salaryData.preview?.summary}</p>
                    <Button onClick={() => navigate("/app?upgrade=true")}>
                      Upgrade to Premium
                    </Button>
                  </div>
                )}

                {salaryData && !salaryData.isPremiumLocked && (
                  <div className="space-y-6">
                    {/* Salary Range */}
                    <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-muted-foreground">Minimum</p>
                          <p className="text-2xl font-bold text-green-700">
                            {salaryData.salary_range?.currency} {salaryData.salary_range?.min?.toLocaleString()}
                          </p>
                        </div>
                        <div className="border-x border-green-200">
                          <p className="text-sm text-muted-foreground">Median</p>
                          <p className="text-3xl font-bold text-green-800">
                            {salaryData.salary_range?.currency} {salaryData.salary_range?.median?.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Maximum</p>
                          <p className="text-2xl font-bold text-green-700">
                            {salaryData.salary_range?.currency} {salaryData.salary_range?.max?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Confidence */}
                    <div className="flex items-center gap-2">
                      <Badge variant={salaryData.confidence === "high" ? "default" : salaryData.confidence === "medium" ? "secondary" : "outline"}>
                        {salaryData.confidence} confidence
                      </Badge>
                      <span className="text-sm text-muted-foreground">{salaryData.confidence_explanation}</span>
                    </div>
                    
                    {/* Factors */}
                    <div>
                      <h4 className="font-semibold mb-3">Key Factors</h4>
                      <div className="flex flex-wrap gap-2">
                        {salaryData.factors?.map((f, i) => (
                          <Badge key={i} variant="outline">{f}</Badge>
                        ))}
                      </div>
                    </div>
                    
                    {/* Negotiation Tips */}
                    <div>
                      <h4 className="font-semibold mb-3">Negotiation Tips</h4>
                      <ul className="space-y-2">
                        {salaryData.negotiation_tips?.map((tip, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Star className="w-4 h-4 text-amber-500 mt-0.5" />
                            <span className="text-sm">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Disclaimer */}
                    <p className="text-xs text-muted-foreground bg-muted/30 p-3 rounded">
                      {salaryData.disclaimer} Source: {salaryData.source}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Similar Profiles Tab */}
          <TabsContent value="network">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  People in Similar Roles
                  {!isPremium && <Badge variant="outline" className="ml-2"><Lock className="w-3 h-3 mr-1" /> Premium</Badge>}
                </CardTitle>
                <CardDescription>
                  Discover professionals in similar roles and grow your network
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!candidateInsights && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="font-semibold mb-2">Network Smarter</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Get profile archetypes and networking strategies for your target role.
                    </p>
                    <Button onClick={getCandidateInsights} disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          Get Insights
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {candidateInsights?.isPremiumLocked && (
                  <div className="text-center py-8 bg-muted/30 rounded-lg">
                    <Lock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Premium Feature</h3>
                    <p className="text-muted-foreground mb-4">{candidateInsights.preview?.summary}</p>
                    <Button onClick={() => navigate("/app?upgrade=true")}>
                      Upgrade to Premium
                    </Button>
                  </div>
                )}

                {candidateInsights && !candidateInsights.isPremiumLocked && (
                  <div className="space-y-6">
                    {/* Profile Archetypes */}
                    <div>
                      <h4 className="font-semibold mb-3">Profile Archetypes</h4>
                      <div className="grid gap-4">
                        {candidateInsights.archetypes?.map((arch, i) => (
                          <div key={i} className="p-4 bg-muted/30 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Users className="w-5 h-5 text-blue-600" />
                              </div>
                              <div className="flex-1">
                                <h5 className="font-semibold">{arch.title}</h5>
                                <p className="text-sm text-muted-foreground mt-1">{arch.description}</p>
                                <div className="mt-3 space-y-1">
                                  <p className="text-xs"><strong>Background:</strong> {arch.common_background}</p>
                                  <p className="text-xs"><strong>Career Path:</strong> {arch.career_path}</p>
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {arch.typical_skills?.map((skill, si) => (
                                      <Badge key={si} variant="secondary" className="text-xs">{skill}</Badge>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* LinkedIn Search Queries */}
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Search className="w-4 h-4" />
                        LinkedIn Search Queries
                      </h4>
                      <div className="space-y-3">
                        {candidateInsights.linkedin_search_queries?.map((sq, i) => (
                          <div key={i} className="p-3 bg-muted/30 rounded-lg">
                            <p className="text-sm text-muted-foreground mb-2">{sq.purpose}</p>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 text-xs bg-white p-2 rounded border overflow-x-auto">
                                {sq.query}
                              </code>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyToClipboard(sq.query, i)}
                              >
                                {copiedQuery === i ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(sq.query)}`, '_blank')}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Outreach Questions */}
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Outreach Questions
                      </h4>
                      <ul className="space-y-2">
                        {candidateInsights.outreach_questions?.map((q, i) => (
                          <li key={i} className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg">
                            <span className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-xs font-medium text-primary">
                              {i + 1}
                            </span>
                            <span className="text-sm">{q}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Networking Tips */}
                    <div>
                      <h4 className="font-semibold mb-3">Networking Tips</h4>
                      <ul className="space-y-2">
                        {candidateInsights.networking_tips?.map((tip, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Star className="w-4 h-4 text-amber-500 mt-0.5" />
                            <span className="text-sm">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default InsightsPage;
